import { TableHeaderCell, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowUpRegular, ArrowDownRegular, ArrowSortRegular } from '@fluentui/react-icons';
import type { SortDirection } from '../../hooks/useTableFeatures';
import type { ReactNode, CSSProperties } from 'react';
import { handleActivateKeyDown, toAriaSort } from '../../utils/a11y';

const useStyles = makeStyles({
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sortIcon: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  sortIconActive: {
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
  },
  sortIconHidden: {
    fontSize: '12px',
    opacity: 0,
    transition: 'opacity 0.15s',
  },
});

interface SortableHeaderProps {
  children: ReactNode;
  sortKey: string;
  sortDirection: SortDirection | null;
  onSort: (key: string) => void;
  style?: CSSProperties;
}

export function SortableHeader({
  children,
  sortKey,
  sortDirection,
  onSort,
  style,
}: SortableHeaderProps) {
  const styles = useStyles();

  const handleClick = () => {
    onSort(sortKey);
  };

  const renderSortIcon = () => {
    if (sortDirection === 'asc') {
      return <ArrowUpRegular className={styles.sortIconActive} />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDownRegular className={styles.sortIconActive} />;
    }
    return <ArrowSortRegular className={styles.sortIcon} />;
  };

  return (
    <TableHeaderCell
      className={styles.sortableHeader}
      onClick={handleClick}
      onKeyDown={handleActivateKeyDown(handleClick)}
      tabIndex={0}
      aria-sort={toAriaSort(sortDirection)}
      style={style}
    >
      <div className={styles.headerContent}>
        {children}
        {renderSortIcon()}
      </div>
    </TableHeaderCell>
  );
}
